import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Analytics, setUserId as setAnalyticsUserId } from '@angular/fire/analytics';
import { Auth, signOut } from '@angular/fire/auth';
import { Firestore, addDoc, collection, collectionData, orderBy, query, where, serverTimestamp } from '@angular/fire/firestore';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { TranslateService } from 'src/assets/translate.service';
import { AlertController, LoadingController, ModalController, ToastController, Platform } from '@ionic/angular/standalone';

export class LogItem {
    constructor(
        public title: string | null = null,
        public subtitle: any = null,
        public type: string | null = null,
        public uid: string | null = null,
        public versionNumber: string | null = null,
        public versionCode: string | null = null,
        public packageName: string | null = null,
        public cordova: boolean = false,
        public platform: string | null = null,
        public email: string | null = null,
        public serial: string | null = null,
        public currentUrl: string | null = null,
        public appName: string | null = null,
        public provider: string | null = null,
        public browserType: string | null = null,
        public browserVersion: string | null = null,
        public id: string = '',
        public timeStamp: string = new Date().toISOString()
    ) {
        this.title = title ? (typeof title !== 'string' ? JSON.stringify(title) : title) : title;
        this.subtitle = subtitle ? (typeof subtitle !== 'string' ? JSON.stringify(subtitle) : subtitle) : subtitle;
    }
}

@Injectable({ providedIn: 'root' })
export class LoggerService {
    [x: string]: any;
    private currentToast: HTMLIonToastElement | null = null;
    systemSubscribe: Subscription | undefined;
    private isCordova: boolean = false;
    private offlineLog: LogItem[] = [];
    private currentUrl: string = '';
    private browserInfo: { type: string; version: string };
    private uid: string | null = null;
    private versionCode: string | null = null;
    private versionNumber: string | null = null;
    private packageName: string | null = null;
    private appName: string | null = null;
    private plt: string | null = null;
    private logCollectionName = 'SystemLog';
    private LOGLEVEL: 'NONE' | 'OFFLINE' | 'ANALYTICS' | 'ONLINE' = 'ONLINE';
    private email: string | null = null;
    private serial: string | null = null;
    private provider: string | null = null;

    constructor(
        // Ionic Controllerek
        private alertCtrl: AlertController,
        private toastCtrl: ToastController,
        private loadingCtrl: LoadingController,
        private modalCtrl: ModalController,
        private platform: Platform,
        // Egyéb service-ek
        private translate: TranslateService,
        private analytics: Analytics,
        private firestore: Firestore,
        private auth: Auth,
        private router: Router,
    ) {
        this.init();
        this.plt = window.navigator.platform;
        this.isCordova = this.platform.is('cordova'); // Robusztusabb Cordova detektálás
        this.versionNumber = environment.version;
        this.browserInfo = this.getBrowserInfo();
        // Router események követése
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.currentUrl = event.url;
        });
    }

    private getBrowserInfo(): { type: string; version: string } {
        const userAgent = navigator.userAgent;
        let browserType = 'Unknown';
        let browserVersion = 'Unknown';
        if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) {
            browserType = 'Opera';
            browserVersion = userAgent.includes('OPR/') ? userAgent.split('OPR/')[1].split(' ')[0] : userAgent.split('Opera/')[1].split(' ')[0];
        } else if (userAgent.includes('Edg/')) {
            browserType = 'Edge';
            browserVersion = userAgent.split('Edg/')[1].split(' ')[0];
        } else if (userAgent.includes('Edge/')) {
            browserType = 'Edge (Legacy)';
            browserVersion = userAgent.split('Edge/')[1].split(' ')[0];
        } else if (userAgent.includes('Firefox/')) {
            browserType = 'Firefox';
            browserVersion = userAgent.split('Firefox/')[1].split(' ')[0];
        } else if (userAgent.includes('SamsungBrowser/')) {
            browserType = 'Samsung Browser';
            browserVersion = userAgent.split('SamsungBrowser/')[1].split(' ')[0];
        } else if (userAgent.includes('Chrome/')) {
            browserType = 'Chrome';
            browserVersion = userAgent.split('Chrome/')[1].split(' ')[0];
        } else if (userAgent.includes('Safari/')) {
            browserType = 'Safari';
            const versionMatch = userAgent.match(/Version\/(\d+\.\d+)/);
            browserVersion = versionMatch ? versionMatch[1] : 'Unknown';
        } else if (userAgent.includes('MSIE ')) {
            browserType = 'Internet Explorer';
            browserVersion = userAgent.split('MSIE ')[1].split(';')[0];
        } else if (userAgent.includes('Trident/')) {
            browserType = 'Internet Explorer';
            browserVersion = '11.0';
        }
        return { type: browserType, version: browserVersion };
    }

    private init(): Promise<void> {
        return new Promise(async (resolve) => {
            try {
                const offlineLog = await this.loadLog();
                this.offlineLog = offlineLog ? offlineLog : [];
                resolve();
            } catch (error) {
                this.offlineLog = [];
                resolve();
            }
        });
    }

    // --- UI metódusok (Ionic) ---
    async showToast(message: string = '', duration: number = 2000, position: 'top' | 'bottom' | 'middle' = 'bottom', color: string = 'primary') {
        // Ha már van egy aktív toast, ne nyissunk újat
        if (this.currentToast) {
            await this.currentToast.dismiss();
        }
        this.currentToast = await this.toastCtrl.create({
            message: this.translate.instant(message),
            duration: duration,
            position: position,
            color: color,
            buttons: [{ icon: 'close', role: 'cancel' }]
        });
        await this.currentToast.present();
        await this.currentToast.onDidDismiss();
        this.currentToast = null;
    }

    async closeModal() {
        try {
            // Bezárja a legfelső overlay-t (Modal, Alert, Loading, stb.)
            const topOverlay = await this.modalCtrl.getTop() || await this.alertCtrl.getTop() || await this.loadingCtrl.getTop();
            if (topOverlay) {
                await topOverlay.dismiss();
            }
        } catch (e) {
            // Hiba esetén logol, de nem áll le
        }
    }

    async handleError(error: any, show = false, errorText?: string) {
        const terr = new Error();
        const header = error.code || error.header || this.translate.instant('ERROR');
        const title = error.msg || error.message || error.data || error.error || error || this.translate.instant('SOMETHING_WRONG');
        let subtitle: string;
        if (error.error && typeof error.error === 'object') {
            subtitle = error.error.error_message || error.error.error || this.translate.instant('SOMETHING_WRONG');
        } else {
            subtitle = title;
        }
        if (errorText) {
            subtitle = errorText;
        }

        if (!JSON.stringify(error)?.includes('permission-denied')) {
            this.addLogItem(title, error, 'error');
        }

        if (show) {
            const alert = await this.alertCtrl.create({
                header: header,
                message: this.translate.instant(subtitle),
                buttons: [this.translate.instant('OK')]
            });
            await alert.present();
            await alert.onDidDismiss();
        }
        try {
            const bool = JSON.stringify(error).includes('permission-denied');
            if (bool) {
                // this.showToast(this.translate.instant('SOMETHING_WRONG'), 2500, 'top');
            }
        } catch (error) {
            console.error(error);
        }
    }

    async presentLoading(message?: string): Promise<void> {
        await this.dismissLoading(); // Előző bezárása, ha van
        const loading = await this.loadingCtrl.create({
            message: message ? this.translate.instant(message) : this.translate.instant('PLEASE_WAIT'),
            spinner: 'circles'
        });
        await loading.present();
    }

    async dismissLoading(): Promise<void> {
        try {
            await this.loadingCtrl.dismiss();
        } catch (e) {
            // Nincs mit bezárni, ez nem hiba.
        }
    }

    presentConfirmAlert(header: string, message: string, onlyOkButton?: boolean, subHeader?: string, customOkayText?: string): Promise<void> {
        return new Promise(async (resolve, reject) => {
            const okText = customOkayText || this.translate.instant('OK');
            const cancelText = this.translate.instant('CANCEL');
            const buttons: any[] = [{ text: okText, handler: () => resolve() }];

            if (!onlyOkButton) {
                buttons.unshift({ text: cancelText, role: 'cancel', handler: () => reject() });
            }

            const alert = await this.alertCtrl.create({
                header: this.translate.instant(header),
                subHeader: subHeader ? this.translate.instant(subHeader) : undefined,
                message: this.translate.instant(message),
                backdropDismiss: false,
                buttons: buttons
            });

            await alert.present();
        });
    }

    // --- Getter/Setter és Logikai metódusok (változatlan) ---
    getOfflineLog() {
        return this.offlineLog;
    }

    async setUserId(uid: string) {
        this.uid = uid;
        try {
            await setAnalyticsUserId(this.analytics, uid);
            if (this.LOGLEVEL === 'ONLINE') {
                this.uploadOldLog();
            }
        } catch (error) {
            console.error(error);
        }
    }

    logoutUser() {
        return signOut(this.auth);
    }

    getUserId() {
        return this.uid;
    }

    setCordova(cordova: boolean) {
        this.isCordova = cordova;
    }

    getCordova() {
        return this.isCordova;
    }

    setVersionCode(versionCode: string) {
        this.versionCode = versionCode;
    }

    getVersionCode(): string | null {
        return this.versionCode;
    }

    setVersionNumber(versionNumber: string) {
        this.versionNumber = versionNumber;
    }

    getVersionNumber(): string {
        return this.versionNumber || environment.version;
    }

    setPackageName(packageName: string) {
        this.packageName = packageName;
    }

    getPackageName(): string | null {
        return this.packageName;
    }

    getPlt(): string | null {
        return this.plt;
    }

    setAppName(appName: string) {
        this.appName = appName;
    }

    getAppName(): string | null {
        return this.appName;
    }

    setEmail(email: string) {
        this.email = email;
    }

    setSerial(serial: string) {
        this.serial = serial;
    }

    setProvider(provider: string) {
        this.provider = provider || 'NO_DATA';
    }

    getProvider() {
        return this.provider;
    }

    logAnalytics(eventName: string, params: any = {}) {
        params.uid = this.uid;
        params.when = new Date().toISOString();
        params.versionNumber = this.versionNumber;
        params.versioncode = this.versionCode;
        params.packageName = this.packageName;
    }

    getSystemLog(uid: string, startDate: Date, endDate: Date, onlyErrors: boolean = false) {
        const logCollection = collection(this.firestore, this.logCollectionName);
        const queryConstraints = [
            where('uid', '==', uid),
            where('timeStamp', '<', endDate.toISOString()),
            where('timeStamp', '>=', startDate.toISOString()),
            orderBy('timeStamp', 'desc')
        ];
        if (onlyErrors) {
            queryConstraints.push(where('type', '==', 'error'));
        }
        const q = query(logCollection, ...queryConstraints);
        return collectionData(q);
    }

    private async uploadOldLog() {
        if (this.offlineLog.length > 0) {
            let error = null;
            for (const oldLogItem of this.offlineLog) {
                const newLogItem = new LogItem(
                    oldLogItem.title,
                    oldLogItem.subtitle,
                    oldLogItem.type,
                    this.uid,
                    this.versionNumber,
                    this.versionCode,
                    this.packageName,
                    this.isCordova,
                    this.plt,
                    this.email,
                    this.serial,
                    this.currentUrl || this.router.url,
                    this.appName,
                    this.provider,
                    this.browserInfo.type,
                    this.browserInfo.version
                );
                try {
                    await this.addSystemLog(newLogItem, false);
                } catch (e) {
                    error = e;
                }
            }
            if (!error) {
                this.offlineLog = [];
                this.storeLog(this.offlineLog);
            }
        }
    }

    async addLogItem(title: string | null = null, subtitle: any = null, type: string = 'info') {
        const item = new LogItem(
            title,
            subtitle,
            type,
            this.uid,
            this.versionNumber,
            this.versionCode,
            this.packageName,
            this.isCordova,
            this.plt,
            this.email,
            this.serial,
            this.currentUrl || this.router.url,
            this.appName,
            this.provider,
            this.browserInfo.type,
            this.browserInfo.version
        );
        if (title?.toUpperCase().includes('OPENED')) {
            return;
        }
        try {
            await this.addSystemLog(item);
        } catch (error) {
            console.error(error);
        }
    }

    private async addSystemLog(item: LogItem, isNew: boolean = true): Promise<void> {
        let error = null;
        let analytics = false;
        if (this.LOGLEVEL === 'NONE') {
            return;
        }
        if (this.LOGLEVEL === 'ANALYTICS') {
            analytics = true;
        }
        if (this.LOGLEVEL === 'ONLINE') {
            analytics = true;
            try {
                const logCollection = collection(this.firestore, this.logCollectionName);
                // Firestore generates the ID, so we don't need to create one.
                const docRef = await addDoc(logCollection, { ...item, serverTimeStamp: serverTimestamp() });
                item.id = docRef.id;
            } catch (e) {
                error = e;
            }
        }
        if (this.LOGLEVEL === 'OFFLINE' || (error && isNew)) {
            this.pushOfflineLog(item);
        }
        if (analytics) {
            const eventName = item.title?.toLowerCase().replace(/\s+/g, '_') || 'log_item';
            this.logAnalytics(item.type === 'error' ? 'error_happened' : eventName, {
                log_title: item.title,
            });
        }
        if (error) {
            throw error;
        }
    }

    private async pushOfflineLog(item: LogItem) {
        try {
            this.offlineLog.push(item);
            await this.storeLog(this.offlineLog);
        } catch (error) {
            console.error(error);
        }
    }

    private loadLog(): Promise<LogItem[]> {
        return Promise.resolve(JSON.parse(localStorage.getItem('log') || '[]'));
    }

    private storeLog(log: LogItem[]): Promise<LogItem[]> {
        localStorage.setItem('log', JSON.stringify(log));
        return Promise.resolve(log);
    }
}